import TableHead from '@/layouts/layout-table/layout-table-head';
import TableRow from '@/layouts/layout-table/layout-table-row';
import SortButton from '@ui/buttons/sort-button/sort-button';
import type { Sort } from '../../types';
import './registrants-table-header.css';
import { SortDirection } from '@ui/buttons/sort-button/types';
import { useSearchParams } from 'react-router-dom';
import { SortToOrderOptionsEnum } from '../../utils';
import { ICONS } from '@ui/icons/types';
import Checkbox from '@ui/checkbox/checkbox';
import { useAppSelector } from '@/store/hooks';
import { selectIsCourseEnabled } from '@/store/slices/project/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';

type RegistrantsTableHeaderProps = {
  sort: Sort | undefined;
  setSort: (sort: Sort | undefined) => void;
  isAllSelected: boolean;
  selectAll: () => void;
  canEdit?: boolean;
};

const getSortState = (sortBy: Sort['sortBy'], sort: Sort | undefined) => {
  if (sort?.sortBy === sortBy) {
    return sort.sortOrder === 'asc' ? SortDirection.up : SortDirection.down;
  }

  return SortDirection.none;
};

export default function RegistrantsTableHeader({
  sort,
  isAllSelected,
  selectAll,
  setSort,
  canEdit = true,
}: RegistrantsTableHeaderProps) {
  const coursesEnabled = useAppSelector(selectIsCourseEnabled);

  const [params, setParams] = useSearchParams();
  const clickSort = (sortBy: Sort['sortBy']) => () => {
    let newSort: Sort | null = {
      sortBy: sortBy,
      sortOrder: 'asc',
    };

    // new option, default to selecting this option in ascending order
    if (!sort || sort.sortBy !== sortBy) {
      params.set('sort', SortToOrderOptionsEnum(newSort));
    } else {
      const isAsc = sort.sortOrder === 'asc';
      const isDesc = sort.sortOrder === 'desc';

      // if already in ascending order, switch to descending order
      if (isAsc) {
        newSort.sortOrder = 'desc';
        params.set('sort', SortToOrderOptionsEnum(newSort));
      } else if (isDesc) {
        // if already in descending order, remove sort
        // by going back to default
        newSort = null;
        params.delete('sort');
      }
    }
    setParams(Object.fromEntries(params));
    if (setSort) {
      setSort(newSort || undefined);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      selectAll();
    }
  };

  return (
    <TableHead
      border={0}
      className="registrants-table-header"
      style={{ borderTop: 'none', position: 'sticky', top: 0, backgroundColor: 'var(--gray-1000)', zIndex: 2 }}
    >
      <TableRow>
        <OptionalComponent display={canEdit}>
          <th>
            <Checkbox
              checked={isAllSelected}
              onChange={handleSelectAll}
              value={isAllSelected ? 'all' : ''}
              selectedIconName={ICONS.ROUND_CHECKBOX_SELECTED}
              unselectedIconName={ICONS.ROUND_CHECKBOX_UNSELECTED}
            />
          </th>
        </OptionalComponent>
        <th>
          <div className="flex ai-center">
            <span>FULL NAME</span>{' '}
            <SortButton onClick={clickSort('email')} sortDirection={getSortState('email', sort)} />
          </div>
        </th>
        <th>
          <div className="flex ai-center">
            <span>SOURCE</span>{' '}
            <SortButton onClick={clickSort('reg_status')} sortDirection={getSortState('reg_status', sort)} />
          </div>
        </th>
        <th>
          <div className="flex ai-center">
            <span>REG. DATE</span>{' '}
            <SortButton onClick={clickSort('created')} sortDirection={getSortState('created', sort)} />
          </div>
        </th>
        <th>
          <div className="flex ai-center">
            <span>LAST LOGIN</span>{' '}
            <SortButton onClick={clickSort('last_login')} sortDirection={getSortState('last_login', sort)} />
          </div>
        </th>
        <th>
          <div className="flex ai-center">
            <span>WATCHTIME</span>{' '}
            <SortButton onClick={clickSort('watchtime')} sortDirection={getSortState('watchtime', sort)} />
          </div>
        </th>
        <th>
          <div className="flex ai-center">
            <span>AVG. STAR SCORE</span>{' '}
            <SortButton onClick={clickSort('star_score')} sortDirection={getSortState('star_score', sort)} />
          </div>
        </th>
        {coursesEnabled && (
          <>
            <th>
              <div className="flex ai-center">
                <span>COURSES STATUS </span>
                <SortButton
                  onClick={clickSort('course_completion')}
                  sortDirection={getSortState('course_completion', sort)}
                />
              </div>
            </th>
            <th></th>
          </>
        )}
      </TableRow>
    </TableHead>
  );
}
